import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './contexts/AuthContext';
import {
  dashboardService,
  campaignService
} from './lib/supabaseServices';
import {
  LayoutDashboard,
  Send,
  Users,
  MessageSquare,
  Settings,
  Inbox,
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Upload,
  Bell,
  CheckCircle,
  Menu,
  X,
  Target,
  MessageCircle,
  User,
  Instagram,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  LogOut,
  Moon,
  Sun
} from 'lucide-react';
import CampaignsPage from './DashboardPages/Campaigns';
import CampaignCreate from './DashboardPages/CampaignCreate';
import LeadsPage from './DashboardPages/Leads';
import AccountsPage from './DashboardPages/Accounts';
import InboxPage from './DashboardPages/Inbox';
import SettingsPage from './DashboardPages/Settings';
import PricingPage from './DashboardPages/Pricing';

// Enhanced Stats Card Component with better visuals
const StatsCard = React.memo(({ title, value, icon: Icon, darkMode, loading = false, subtitle, className = '' }) => (
  <motion.div
    whileHover={{ y: -4, scale: 1.02 }}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className={`${className} ${darkMode ? 'bg-surface border-border shadow-xl shadow-black/10' : 'bg-white border-gray-200 shadow-lg'} rounded p-3 lg:p-6 border backdrop-blur-sm`}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <p className={`text-xs lg:text-sm font-medium ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>{title}</p>
          <motion.div
            initial={{ opacity: 0, rotate: -180 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`w-8 h-8 lg:w-10 lg:h-10 shrink-0 ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-blue-500 to-purple-600'} rounded flex items-center justify-center`}
          >
            <Icon className="w-4 h-4 lg:w-5 lg:h-5 text-primary" />
          </motion.div>
        </div>
        
        <motion.div 
          key={value}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-2"
        >
          {loading ? (
            <div className={`w-20 h-9 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded animate-pulse`}></div>
          ) : (
            <p className={`text-2xl lg:text-3xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
              {value}
            </p>
          )}
        </motion.div>

        {subtitle && !loading && (
          <p className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'}`}>{subtitle}</p>
        )}
        
        {loading && (
          <div className={`w-20 h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded animate-pulse mt-1`}></div>
        )}
      </div>
    </div>
  </motion.div>
));

// Overview Page Component (extracted to prevent re-mounting on dropdown state changes)
const OverviewPage = React.memo(({ 
  darkMode, 
  error, 
  essentialStats, 
  loading, 
  campaigns, 
  recentActivity, 
  getActivityIcon, 
  navigate
}) => {
  const activeCampaignsCount = campaigns.filter(c => c.status === 'active' || c.status === 'paused').length;
  const totalCampaignsCount = campaigns.length;

  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-4 lg:p-6"
  >
    {/* Error Message */}
    {error && (
      <div className={`mb-6 p-4 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded flex items-center space-x-2`}>
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    )}

    {/* Essential Stats */}
    <motion.div 
      key="stats-grid" // Stable key to prevent re-initialization
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, staggerChildren: 0.1 }}
      className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 mb-6"
    >
      {essentialStats.map((stat, index) => (
        <StatsCard key={`stat-${index}`} {...stat} darkMode={darkMode} loading={loading}
          className={essentialStats.length % 2 === 1 && index === essentialStats.length - 1 ? 'col-span-2 lg:col-span-1' : ''} />
      ))}
    </motion.div>

    {/* Main Content */}
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Campaigns Section - 2/3 width */}
      <div className="xl:col-span-2">
        <div className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
              Active Campaigns 
              <span className={`ml-2 text-sm font-normal ${darkMode ? 'text-secondary' : 'text-gray-500'}`}>
                ({activeCampaignsCount} active • {totalCampaignsCount} total)
              </span>
            </h3>
            <Link
              to="/dashboard/campaigns"
              className={`${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover shadow-lg shadow-accent/25' : 'bg-gradient-to-r from-blue-500 to-purple-600'} text-primary px-4 py-2 rounded text-sm font-semibold hover:shadow-lg transition-all duration-300 flex items-center space-x-2`}
            >
              <Plus className="w-4 h-4" />
              <span>New Campaign</span>
            </Link>
          </div>
          
          <div className="space-y-4">
            {loading ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="space-y-4"
              >
                {[1, 2, 3].map((i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className={`${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded h-32 animate-pulse`}
                  ></motion.div>
                ))}
              </motion.div>
            ) : error ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className={`p-4 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded`}
              >
                <p>Error loading campaigns: {error}</p>
              </motion.div>
            ) : campaigns.filter(c => c.status === 'active' || c.status === 'paused').length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className={`text-center py-8 ${darkMode ? 'text-secondary' : 'text-gray-500'}`}
              >
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className={`w-16 h-16 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full flex items-center justify-center mx-auto mb-4`}
                >
                  <Send className="w-8 h-8" />
                </motion.div>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-lg font-medium mb-2"
                >
                  No active campaigns yet
                </motion.p>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="mb-4"
                >
                  Create your first campaign to start reaching out to prospects
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <Link
                    to="/dashboard/campaigns"
                    className={`${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-blue-500 to-purple-600'} text-primary px-6 py-3 rounded font-semibold hover:shadow-lg transition-all duration-300`}
                  >
                    Create Campaign
                  </Link>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="campaigns-list" // Stable key to prevent re-initialization
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, staggerChildren: 0.1 }}
                className="space-y-4"
              >
                {campaigns.filter(c => c.status === 'active' || c.status === 'paused').map((campaign, index) => {
                  // Calculate real lead count from connected leads lists
                  const totalLeads = campaign.campaign_leads_lists?.reduce((sum, cll) => 
                    sum + (cll.leads_lists?.total_leads || 0), 0) || 0;
                  const progressPercentage = totalLeads > 0 ? (campaign.messages_sent / totalLeads) * 100 : 0;
                  
                  return (
                    <motion.div
                      key={campaign.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -2, scale: 1.01 }}
                      className={`${darkMode ? 'border-border hover:shadow-xl hover:shadow-accent/20' : 'border-gray-200 hover:shadow-lg'} border rounded overflow-hidden transition-all duration-300 cursor-pointer group`}
                      onClick={() => navigate('/dashboard/campaigns')}
                      title="Click to manage campaigns"
                    >
                      <div className="p-4">
                        {/* Single Row Layout */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-3">
                          {/* Campaign Name */}
                          <h4 className={`font-semibold truncate min-w-0 flex-1 ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                            {campaign.name}
                          </h4>

                          {/* Status Pill */}
                          <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded-full capitalize ${
                            campaign.status === 'active'
                              ? darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-green-100 text-green-800'
                              : darkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {campaign.status}
                          </span>

                          {/* Stats */}
                          <div className="flex items-center gap-x-4 gap-y-1 w-full">
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>Leads:</span>
                              <span className={`text-sm font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'}`}>{totalLeads}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>Contacted:</span>
                              <span className={`text-sm font-semibold ${darkMode ? 'text-accent' : 'text-accent'}`}>{campaign.leads_contacted || campaign.messages_sent || 0}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>Messages:</span>
                              <span className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>{campaign.total_tasks_completed || 0}</span>
                            </div>
                          </div>
                          
                        </div>

                        {/* Progress Bar */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>Progress</span>
                            <span className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>{progressPercentage.toFixed(1)}%</span>
                          </div>
                          <div className={`w-full ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full h-1.5`}>
                            <div 
                              className={`bg-gradient-to-r from-accent to-accent-hover h-1.5 rounded-full transition-all duration-300`}
                              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity - 1/3 width */}
      <div className="xl:col-span-1">
        <div className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-sm border`}>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-6`}>Recent Activity</h3>
          <motion.div 
            key="activity-list" // Stable key to prevent re-initialization
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, staggerChildren: 0.05 }}
            className="space-y-4 max-h-96 overflow-y-auto"
          >
            {recentActivity.map((activity, index) => (
              <motion.div 
                key={activity.id} 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`flex items-start space-x-3 p-3 rounded ${darkMode ? 'hover:bg-elevated' : 'hover:bg-gray-50'} transition-colors cursor-pointer`}
              >
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 + 0.1 }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    activity.color === 'blue' ? (darkMode ? 'bg-blue-400/20' : 'bg-blue-100') :
                    activity.color === 'green' ? (darkMode ? 'bg-green-400/20' : 'bg-green-100') :
                    activity.color === 'purple' ? (darkMode ? 'bg-purple-400/20' : 'bg-purple-100') :
                    activity.color === 'orange' ? (darkMode ? 'bg-orange-400/20' : 'bg-orange-100') :
                    activity.color === 'cyan' ? (darkMode ? 'bg-accent/20' : 'bg-cyan-100') :
                    activity.color === 'indigo' ? (darkMode ? 'bg-indigo-400/20' : 'bg-indigo-100') :
                    darkMode ? 'bg-gray-400/20' : 'bg-gray-100'
                  }`}
                >
                  {getActivityIcon(activity.type, activity.color)}
                </motion.div>
                <div className="flex-1 min-w-0">
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                    className={`text-sm ${darkMode ? 'text-primary' : 'text-gray-900'} leading-relaxed`}
                  >
                    {activity.text}
                  </motion.p>
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 + 0.3 }}
                    className={`text-xs ${darkMode ? 'text-muted' : 'text-gray-500'} mt-1`}
                  >
                    {activity.time}
                  </motion.p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>

  </motion.div>
);
});

const Dashboard = ({ darkMode, toggleDarkMode }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  
  // Dashboard data states
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState(null);
  
  // Stats data - only 3 stats now
  const [essentialStats, setEssentialStats] = useState([
    { title: 'Total Leads', value: '0', subtitle: '', icon: Users },
    { title: 'Leads Contacted', value: '0', subtitle: '', icon: MessageCircle },
    { title: 'Messages Sent', value: '0', subtitle: '', icon: Send }
  ]);

  const [recentActivity, setRecentActivity] = useState([]);

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard, current: location.pathname === '/dashboard' },
    { name: 'Campaigns', href: '/dashboard/campaigns', icon: Send, current: location.pathname === '/dashboard/campaigns' },
    { name: 'Leads', href: '/dashboard/leads', icon: Users, current: location.pathname === '/dashboard/leads' },
    { name: 'Accounts', href: '/dashboard/accounts', icon: Instagram, current: location.pathname === '/dashboard/accounts' },
    { name: 'Inbox', href: '/dashboard/inbox', icon: Inbox, current: location.pathname === '/dashboard/inbox' },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings, current: location.pathname === '/dashboard/settings' },
  ];

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownOpen && !event.target.closest('.user-dropdown')) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add a minimum loading time to prevent flashing
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 1500));

      // Load overview stats
      try {
        const stats = await dashboardService.getOverviewStats();
        await minLoadTime; // Ensure minimum loading time
        
        // Use the new stats structure from the service
        const leadsContacted = stats.leadsContacted || 0;
        const totalMessages = stats.totalMessages || 0;
        
        setEssentialStats([
          { 
            title: 'Total Leads', 
            value: (stats.totalLeads || 0).toLocaleString(), 
            subtitle: `${leadsContacted} contacted so far`,
            icon: Users 
          },
          { 
            title: 'Leads Contacted', 
            value: leadsContacted.toLocaleString(), 
            subtitle: 'Unique leads messaged',
            icon: MessageCircle 
          },
          { 
            title: 'Messages Sent', 
            value: totalMessages.toLocaleString(), 
            subtitle: 'Including follow-ups',
            icon: Send 
          }
        ]);
      } catch (err) {
        console.log('Using default stats due to service error:', err.message);
        await minLoadTime; // Still ensure minimum loading time
      }

      // Load campaigns
      try {
        const campaignsData = await campaignService.getCampaigns();
        setCampaigns(campaignsData);
      } catch (err) {
        console.log('Using empty campaigns due to service error:', err.message);
        setCampaigns([]);
      }


      // Load recent activity
      try {
        const activityData = await dashboardService.getRecentActivity(8);
        setRecentActivity(activityData);
      } catch (err) {
        console.log('Using empty activity due to service error:', err.message);
        setRecentActivity([]);
      }

      // Mark data as loaded for transition effects
      setDataLoaded(true);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      // Add a slight delay before removing loading state for smooth transition
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  };


  const getActivityIcon = (type, color) => {
    const getIconColorClass = (color) => {
      switch(color) {
        case 'blue': return 'text-blue-600';
        case 'green': return 'text-green-600';
        case 'purple': return 'text-purple-600';
        case 'orange': return 'text-orange-600';
        case 'cyan': return 'text-accent';
        case 'indigo': return 'text-indigo-600';
        default: return 'text-gray-600';
      }
    };
    
    const iconClasses = `w-4 h-4 ${getIconColorClass(color)}`;
    
    switch(type) {
      case 'lead_messaged': return <Send className={iconClasses} />;
      case 'lead_responded': return <MessageCircle className={iconClasses} />;
      case 'followup_sent': return <MessageSquare className={iconClasses} />;
      case 'leads_list_created': return <Upload className={iconClasses} />;
      case 'leads_generated': return <Target className={iconClasses} />;
      case 'response': return <MessageCircle className={iconClasses} />;
      case 'campaign_started': return <Play className={iconClasses} />;
      case 'campaign_created': return <Plus className={iconClasses} />;
      case 'campaign_paused': return <Pause className={iconClasses} />;
      case 'leads_added': return <Plus className={iconClasses} />;
      case 'account_connected': return <User className={iconClasses} />;
      default: return <Bell className={iconClasses} />;
    }
  };

  // Render appropriate page content based on route
  const renderPageContent = () => {
    if (location.pathname === '/dashboard') {
      return (
        <OverviewPage
          darkMode={darkMode}
          error={error}
          essentialStats={essentialStats}
          loading={loading}
          campaigns={campaigns}
          recentActivity={recentActivity}
          getActivityIcon={getActivityIcon}
          navigate={navigate}
        />
      );
    }
    
    return (
      <Routes>
        <Route path="/campaigns" element={<CampaignsPage darkMode={darkMode} />} />
        <Route path="/campaigns/create" element={<CampaignCreate darkMode={darkMode} />} />
        <Route path="/leads" element={<LeadsPage darkMode={darkMode} />} />
        <Route path="/accounts" element={<AccountsPage darkMode={darkMode} />} />
        <Route path="/inbox" element={<InboxPage darkMode={darkMode} />} />
        <Route path="/settings" element={<SettingsPage darkMode={darkMode} />} />
        <Route path="/pricing" element={<PricingPage darkMode={darkMode} />} />
      </Routes>
    );
  };

  return (
    <div className={`flex h-dvh overflow-hidden ${darkMode ? 'bg-canvas' : 'bg-gray-50'}`}>
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed lg:translate-x-0 lg:static lg:inset-0 flex flex-col w-64 h-full transition-transform duration-300 ease-in-out ${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} border-r z-40`}>
        {/* User Profile Section */}
        <div className={`p-4 border-b ${darkMode ? 'border-border' : 'border-gray-200'}`}>
          <div className="relative user-dropdown">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setUserDropdownOpen(!userDropdownOpen);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded transition-colors ${
                darkMode 
                  ? 'hover:bg-elevated' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-accent to-accent-hover rounded-full flex items-center justify-center">
                  <span className="text-primary text-sm font-semibold">
                    {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="text-left">
                  <div className={`text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-900'}`}>
                    {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-secondary' : 'text-gray-500'}`}>
                    {user?.email}
                  </div>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${userDropdownOpen ? 'rotate-180' : ''} ${darkMode ? 'text-secondary' : 'text-gray-400'}`} />
            </button>

            {/* Dropdown Menu */}
            {userDropdownOpen && (
              <div 
                className={`absolute left-0 right-0 mt-2 ${
                  darkMode 
                    ? 'bg-surface border-border shadow-xl shadow-black/25' 
                    : 'bg-white border-gray-200 shadow-lg'
                } border rounded py-2 z-50`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Theme Toggle */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDarkMode();
                  }}
                  className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${
                    darkMode 
                      ? 'text-secondary hover:bg-elevated hover:text-primary' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {darkMode ? (
                    <>
                      <Sun className="w-4 h-4 mr-3" />
                      Switch to Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="w-4 h-4 mr-3" />
                      Switch to Dark Mode
                    </>
                  )}
                </button>

                {/* Settings Link */}
                <Link
                  to="/dashboard/settings"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserDropdownOpen(false);
                  }}
                  className={`flex items-center px-4 py-2 text-sm transition-colors ${
                    darkMode 
                      ? 'text-secondary hover:bg-elevated hover:text-primary' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Settings className="w-4 h-4 mr-3" />
                  Settings
                </Link>

                {/* Divider */}
                <div className={`my-1 border-t ${darkMode ? 'border-border' : 'border-gray-200'}`}></div>

                {/* Logout */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    setUserDropdownOpen(false);
                    try {
                      await signOut();
                    } catch (error) {
                      console.error('Error signing out:', error);
                    }
                  }}
                  className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${
                    darkMode 
                      ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300' 
                      : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                  }`}
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 p-2 rounded hover:bg-elevated"
          >
            <X className="w-5 h-5 text-secondary" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center space-x-3 px-3 py-2 rounded transition-colors ${
                item.current
                  ? darkMode ? 'bg-accent/20 text-accent' : 'bg-blue-100 text-blue-600'
                  : darkMode ? 'text-secondary hover:text-primary hover:bg-elevated' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <item.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{item.name}</span>
            </Link>
          ))}
        </nav>

      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Mobile top bar (in-flow, so page content never sits under it) */}
        <div className={`lg:hidden flex items-center gap-3 h-14 px-4 border-b shrink-0 z-20 ${
          darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'
        }`}>
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            className={`p-2 -ml-2 rounded ${
              darkMode ? 'text-primary hover:bg-elevated' : 'text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className={`font-bold tracking-tight ${darkMode ? 'text-primary' : 'text-gray-900'}`}>ICEDMS</span>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {renderPageContent()}
        </main>
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;